<template>
  <div class="login-container">
    <div v-if="isMobile && isDingTalk" class="mobile-login">
      <div class="main-content">
        <div class="icon-container">
          <i class="icon-user fa fa-user"></i>
        </div>
        <h1 class="mobile-title">智学云</h1>
        <p class="mobile-description">
          请点击下方按钮授权获取<br>
          <span class="highlight">昵称、头像及 UserID</span><br>
          以便我们为您提供专属服务
        </p>
        <p v-if="ssoLoading" class="sso-tip sso-tip--loading">检测到 SSO 票据，正在完成免登...</p>
        <p v-else-if="ssoError" class="sso-tip sso-tip--error">{{ ssoError }}</p>
      </div>
      <div class="footer-actions">
        <button
          class="btn-primary"
          id="agreeBtn"
          @click="handleMobileLogin"
          :disabled="isLoading || ssoLoading"
          :class="{ 'btn-loading': isLoading || ssoLoading }"
        >
          <span v-if="!isLoading && !ssoLoading" class="btn-text">同意并授权</span>
          <span v-else class="btn-text">正在跳转...</span>
        </button>
      </div>

      <div v-if="isAuthorizingMask || ssoLoading" class="auth-mask">
        <div class="mask-content">
          <div class="spinner-large"></div>
          <p class="mask-text">{{ ssoLoading ? '免登处理中，请稍候' : '授权中，请勿返回' }}</p>
        </div>
      </div>
    </div>

    <div v-if="isMobile && !isDingTalk" class="mobile-hint">
      <i class="fa fa-mobile"></i>
      <p>当前未处于钉钉环境。</p>
      <p>请从 BSG 平台或钉钉内打开学习应用。</p>
      <p v-if="ssoError" class="sso-tip sso-tip--error">{{ ssoError }}</p>
    </div>

    <div v-if="!isMobile" class="pc-login">
      <div class="bg-decoration blob-1"></div>
      <div class="bg-decoration blob-2"></div>

      <div class="login-card">
        <div class="header">
          <h1 class="platform-name">视频学习平台</h1>
          <div class="login-type">
            <i class="fa fa-shield"></i>
            <span>{{ '钉钉安全登录' }}</span>
          </div>
        </div>

        <div v-if="ssoLoading" class="sso-status">
          <i class="el-icon-loading"></i>
          <span>检测到平台票据，正在尝试快捷登录...</span>
        </div>
        <div v-else-if="ssoError" class="sso-status sso-status--error">
          <i class="el-icon-warning-outline"></i>
          <span>{{ ssoError }}，请继续使用钉钉扫码登录。</span>
        </div>

        <div v-show="!isError" class="qr-wrapper">
          <div id="dd-login-container" class="qr-container"></div>

          <div v-if="loading" class="loading-cover">
            <div class="spinner"></div>
            <p>正在加载二维码...</p>
          </div>

          <div class="scan-success-overlay" :class="{ active: isScanSuccess }">
            <div class="success-icon">
              <i class="fa fa-check"></i>
            </div>
            <div class="success-text">扫描成功</div>
          </div>

          <div class="logging-in-overlay" :class="{ active: isLoggingIn }">
            <div class="logging-spinner"></div>
            <p class="logging-text">扫码成功正在登录中...</p>
          </div>
        </div>

        <div v-if="isError" class="error-display">
          <i class="fa fa-exclamation-circle"></i>
          <p>{{ errorMsg }}</p>
          <el-button size="mini" type="primary" plain @click="initPcAuth">重试</el-button>
        </div>

        <div class="footer-hint">
          使用 <strong>钉钉手机客户端</strong> 扫一扫<br>
          即可快速安全登录
        </div>
      </div>

      <div class="page-footer">
        © 2026 视频学习平台. All Rights Reserved.
      </div>
    </div>
  </div>
</template>

<script>
import { exchangeSsoToken, getDingTalkAuthUrl, pollDingTalkLogin } from "@/api/api";
import request from "@/utils/request";

export default {
  name: "Login",
  data() {
    return {
      state: "",
      appKey: "",
      loading: true,
      isError: false,
      errorMsg: "",
      isProcessing: false,
      isMobile: false,
      isDingTalk: false,
      pollTimer: null,
      gotoUrl: "",
      isLoading: false,
      isAuthorizingMask: false,
      isScanSuccess: false,
      isLoggingIn: false,
      ssoLoading: false,
      ssoError: "",
      ssoToken: ""
    };
  },
  async mounted() {
    this.checkEnvironment();

    this.ssoToken = this.getSsoToken();
    if (this.ssoToken) {
      const success = await this.trySsoLogin();
      if (success) {
        return;
      }
    }

    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    const state = urlParams.get("state");

    if (code && state) {
      this.isAuthorizingMask = true;
      this.state = state;
      await this.callBackendCallback(code, state);
      return;
    }

    this.initDingTalkFallback();
  },
  methods: {
    checkEnvironment() {
      const ua = navigator.userAgent.toLowerCase();
      this.isMobile = /android|iphone|ipad/.test(ua);
      this.isDingTalk = ua.indexOf("dingtalk") > -1 || ua.indexOf("aliapp") > -1;
    },
    getSsoToken() {
      if (this.$route.query && this.$route.query.sso_token) {
        return this.$route.query.sso_token;
      }

      const searchToken = new URLSearchParams(window.location.search || "").get("sso_token");
      if (searchToken) {
        return searchToken;
      }

      const hash = window.location.hash || "";
      const queryIndex = hash.indexOf("?");
      if (queryIndex !== -1) {
        return new URLSearchParams(hash.substring(queryIndex + 1)).get("sso_token") || "";
      }

      return "";
    },
    getRedirectPath() {
      return this.$route.query.redirect || "/";
    },
    stripTransientQuery() {
      const url = new URL(window.location.href);
      url.searchParams.delete("sso_token");
      url.searchParams.delete("redirect");
      url.searchParams.delete("code");
      url.searchParams.delete("state");

      let hash = url.hash || "";
      if (hash) {
        const hashContent = hash.substring(1);
        const queryIndex = hashContent.indexOf("?");
        const hashPath = queryIndex === -1 ? hashContent : hashContent.substring(0, queryIndex);
        const hashQuery = new URLSearchParams(queryIndex === -1 ? "" : hashContent.substring(queryIndex + 1));
        hashQuery.delete("sso_token");
        hashQuery.delete("redirect");
        hashQuery.delete("code");
        hashQuery.delete("state");
        const hashQueryString = hashQuery.toString();
        hash = hashPath ? `#${hashPath}${hashQueryString ? `?${hashQueryString}` : ""}` : "";
      }

      window.history.replaceState({}, "", `${url.pathname}${url.search}${hash}`);
    },
    async trySsoLogin() {
      this.ssoError = "";
      this.ssoLoading = true;
      try {
        const res = await exchangeSsoToken({
          ssoToken: this.ssoToken,
          clientType: "front"
        });

        if (res.code === 1000 && res.data && res.data.token) {
          await this.finishLogin(res.data.token, "平台快捷登录成功");
          return true;
        }

        this.ssoError = res.message || "SSO 免登失败";
        return false;
      } catch (error) {
        this.ssoError = "SSO 登录请求失败";
        return false;
      } finally {
        this.ssoLoading = false;
      }
    },
    initDingTalkFallback() {
      if (this.isMobile && this.isDingTalk) {
        this.initMobileAuth();
      } else if (!this.isMobile) {
        this.initPcAuth();
      } else {
        this.loading = false;
      }
    },
    async callBackendCallback(code, state) {
      try {
        await request({
          url: "/dingtalk/callback",
          method: "get",
          params: {
            code,
            state
          }
        });
      } catch (error) {
        // 静默处理，轮询接口会继续兜底
      }

      this.startPolling();
    },
    async initMobileAuth() {
      this.loading = true;

      try {
        const res = await getDingTalkAuthUrl();

        if (res.code === 1000 && res.data?.state && res.data?.appKey) {
          this.state = res.data.state;
          this.appKey = res.data.appKey;
          this.loading = false;

          if (this.isLoading) {
            setTimeout(() => {
              const redirectUri = window.location.origin + "/dingtalk/callback";
              const loginUrl = `https://login.dingtalk.com/oauth2/challenge.htm?` +
                `client_id=${this.appKey}&` +
                `response_type=code&` +
                `scope=openid&` +
                `state=${this.state}&` +
                `prompt=consent&` +
                `redirect_uri=${encodeURIComponent(redirectUri)}`;
              window.location.href = loginUrl;
            }, 100);
          }
        } else {
          this.isLoading = false;
          this.handleError("获取授权参数失败，请重试");
        }
      } catch (e) {
        this.isLoading = false;
        this.handleError(e.message || "获取授权参数失败");
      }
    },
    handleMobileLogin() {
      if (this.isLoading || this.ssoLoading) {
        return;
      }

      if (!this.appKey || !this.state) {
        this.isLoading = true;
        this.initMobileAuth();
        return;
      }

      this.isLoading = true;

      const redirectUri = window.location.origin + "/dingtalk/callback";
      const loginUrl = `https://login.dingtalk.com/oauth2/challenge.htm?` +
        `client_id=${this.appKey}&` +
        `response_type=code&` +
        `scope=openid&` +
        `state=${this.state}&` +
        `prompt=consent&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}`;

      window.location.href = loginUrl;
    },
    async handleAuthCodeSuccess(authCode) {
      if (this.isProcessing) {
        return;
      }
      this.isProcessing = true;
      this.isLoggingIn = true;

      try {
        await request({
          url: "/dingtalk/callback",
          method: "get",
          params: {
            code: authCode,
            state: this.state
          }
        });
      } catch (error) {
        // 静默处理
      }

      this.startPolling();
    },
    startPolling() {
      this.stopPolling();
      let count = 0;

      this.pollTimer = setInterval(async () => {
        count++;

        if (count > 150) {
          this.stopPolling();
          this.handleError("登录超时，请重试");
          return;
        }

        try {
          const res = await pollDingTalkLogin({ state: this.state });

          if (res.code === 1000) {
            if (res.data?.status === "success") {
              this.stopPolling();
              await this.loginSuccess(res.data);
            } else if (res.data?.status === "failed" || res.data?.status === "error") {
              this.stopPolling();
              this.handleError(res.data.message || "登录验证失败");
            }
          }
        } catch (e) {
          // 静默失败，继续轮询
        }
      }, 2000);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    async finishLogin(token, successMessage) {
      this.$store.dispatch("setToken", token);
      await this.$store.dispatch("fetchUserInfo");
      this.$store.dispatch("permission/getPermissions").catch(() => {});
      this.stripTransientQuery();
      this.$message.success(successMessage);
      this.$router.replace(this.getRedirectPath()).catch(() => {});
    },
    async loginSuccess(data) {
      if (!this.isMobile) {
        this.isScanSuccess = true;
      }

      this.isAuthorizingMask = false;
      this.isLoggingIn = false;
      this.isLoading = false;

      if (data.token) {
        await this.finishLogin(data.token, "登录成功");
      }
    },
    handleError(msg) {
      this.loading = false;
      this.isError = true;
      this.errorMsg = msg;
      this.isProcessing = false;
      this.isLoading = false;
      this.isAuthorizingMask = false;
      this.isScanSuccess = false;
      this.isLoggingIn = false;
      this.stopPolling();
    },
    async initPcAuth() {
      this.loading = true;
      this.isError = false;
      this.isProcessing = false;

      try {
        const res = await getDingTalkAuthUrl();
        if (res.code !== 1000 || !res.data?.authUrl || !res.data?.state) {
          throw new Error("授权参数缺失");
        }

        this.gotoUrl = res.data.authUrl;
        this.state = res.data.state;
        this.appKey = res.data.appKey;

        this.renderQrCode();
        this.startPolling();
      } catch (e) {
        this.handleError("服务连接异常");
      }
    },
    renderQrCode() {
      const container = document.getElementById("dd-login-container");
      if (!container) {
        setTimeout(() => this.renderQrCode(), 100);
        return;
      }
      container.innerHTML = "";

      if (typeof window.DTFrameLogin === "undefined") {
        this.handleError("PC 安全组件加载失败");
        return;
      }

      window.DTFrameLogin(
        { id: "dd-login-container", width: 300, height: 300 },
        {
          redirect_uri: window.location.origin + "/dingtalk/callback",
          client_id: this.appKey,
          scope: "openid",
          response_type: "code",
          state: this.state,
          prompt: "consent"
        },
        loginResult => {
          const { authCode } = loginResult;
          this.handleAuthCodeSuccess(authCode);
        },
        errorMsg => {
          this.handleError("扫码失败：" + errorMsg);
        }
      );
      this.loading = false;
    }
  }
};
</script>

<style scoped>
.login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: #f0f2f5;
  box-sizing: border-box;
  position: relative;
}

.login-container .mobile-login {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  min-height: 100vh;
  background-color: #ffffff;
  padding: 0;
  box-sizing: border-box;
}

.login-wrapper {
  width: 360px;
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
  z-index: 1;
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 32px;
  text-align: center;
  animation: fadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.icon-container {
  width: 96px;
  height: 96px;
  background: linear-gradient(135deg, #E6F4FF 0%, #F0F7FF 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 32px;
  box-shadow: 0 8px 24px rgba(0, 137, 255, 0.15);
}

.icon-user {
  font-size: 48px;
  color: #0089ff;
  animation: breathe 2.5s infinite ease-in-out;
}

@keyframes breathe {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.08); }
}

.mobile-title {
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 16px;
  color: #171a1d;
  letter-spacing: -0.5px;
}

.mobile-description {
  font-size: 15px;
  color: #8f959e;
  line-height: 1.6;
  max-width: 260px;
  margin: 0;
}

.highlight {
  color: #0089ff;
  font-weight: 600;
}

.sso-tip {
  margin-top: 18px;
  font-size: 13px;
  line-height: 1.6;
}

.sso-tip--loading {
  color: #2563eb;
}

.sso-tip--error {
  color: #dc2626;
}

.footer-actions {
  width: 100%;
  padding: 32px 32px;
  background: #fff;
  display: flex;
  justify-content: center;
  box-sizing: border-box;
  border-top: 1px solid #f0f0f0;
}

.btn-primary {
  width: 100%;
  max-width: 320px;
  height: 56px;
  border-radius: 28px;
  border: none;
  background-color: #0089ff;
  color: white;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 16px rgba(0, 137, 255, 0.4);
  transition: all 0.2s cubic-bezier(0.25, 0.8, 0.25, 1);
  position: relative;
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Helvetica Neue", Arial, sans-serif;
}

.btn-primary:active {
  background-color: #0070d4;
  transform: scale(0.96);
  box-shadow: 0 4px 10px rgba(0, 137, 255, 0.3);
}

.btn-primary:disabled {
  background-color: #bdd6f7;
  box-shadow: none;
  cursor: not-allowed;
}

.btn-primary.btn-loading {
  opacity: 0.8;
}

.auth-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeInMask 0.3s ease-in-out;
}

@keyframes fadeInMask {
  from { opacity: 0; }
  to { opacity: 1; }
}

.mask-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.spinner-large {
  width: 50px;
  height: 50px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #ffffff;
  animation: spin 1s linear infinite;
}

.mask-text {
  color: #ffffff;
  font-size: 16px;
  font-weight: 500;
  margin: 0;
}

.pc-login {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: auto;
  background: linear-gradient(135deg, #F5F7FA 0%, #E4E9F2 100%);
  position: relative;
  overflow: hidden;
}

.login-card {
  background: #FFFFFF;
  width: 360px;
  padding: 48px 40px;
  border-radius: 16px;
  border: 1px solid #E8E8E8;
  box-shadow: none;
  text-align: center;
  position: relative;
  animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1);
  z-index: 10;
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
}

.header {
  margin-bottom: 24px;
}

.platform-name {
  font-size: 24px;
  font-weight: 600;
  color: #171a1d;
  margin-bottom: 8px;
  letter-spacing: -0.5px;
}

.login-type {
  font-size: 14px;
  color: #646a73;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.login-type i {
  width: 16px;
  height: 16px;
  color: #0089ff;
  font-size: 16px;
}

.sso-status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0 auto 16px;
  padding: 10px 12px;
  border-radius: 12px;
  background: #eef6ff;
  color: #2563eb;
  font-size: 13px;
  line-height: 1.5;
}

.sso-status--error {
  background: #fff1f2;
  color: #dc2626;
}

.qr-wrapper {
  position: relative;
  width: 180px;
  height: 180px;
  margin: 0 auto 24px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 0;
  overflow: hidden;
  transition: all 0.3s ease;
}

.qr-wrapper:hover {
  border-color: #0089ff;
}

.qr-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  filter: drop-shadow(0 0 0 transparent);
}

.loading-cover {
  position: absolute;
  inset: 0;
  background: transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 0;
  z-index: 20;
}

.loading-cover .spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(0, 137, 255, 0.2);
  border-radius: 50%;
  border-top-color: #0089ff;
  animation: spin 0.8s linear infinite;
  margin-bottom: 12px;
}

.loading-cover p {
  font-size: 12px;
  color: #0089ff;
  margin: 0;
}

.scan-success-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: transparent;
  border-radius: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  z-index: 25;
}

.scan-success-overlay.active {
  opacity: 1;
  visibility: visible;
}

.success-icon {
  width: 48px;
  height: 48px;
  background: #E6F7FF;
  color: #0089ff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  font-size: 24px;
}

.success-text {
  font-size: 14px;
  color: #0089ff;
  font-weight: 500;
}

.logging-in-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  z-index: 25;
}

.logging-in-overlay.active {
  opacity: 1;
  visibility: visible;
}

.logging-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(0, 137, 255, 0.2);
  border-radius: 50%;
  border-top-color: #0089ff;
  animation: spin 0.8s linear infinite;
  margin-bottom: 12px;
}

.logging-text {
  font-size: 14px;
  color: #0089ff;
  font-weight: 500;
  margin: 0;
}

.error-display {
  color: #f56c6c;
  padding: 20px 0;
  text-align: center;
}

.error-display i {
  font-size: 24px;
  margin-bottom: 8px;
  display: block;
}

.error-display p {
  font-size: 14px;
  margin-bottom: 16px;
}

.footer-hint {
  font-size: 13px;
  color: #8f959e;
  line-height: 1.5;
  margin-top: 16px;
}

.footer-hint strong {
  color: #646a73;
  font-weight: 500;
}

.page-footer {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 12px;
  color: #c0c4cc;
  text-align: center;
  z-index: 0;
}

.bg-decoration {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  z-index: 1;
  opacity: 0.6;
  pointer-events: none;
}

.blob-1 {
  width: 300px;
  height: 300px;
  background: rgba(0, 137, 255, 0.08);
  top: -100px;
  left: -100px;
}

.blob-2 {
  width: 400px;
  height: 400px;
  background: rgba(0, 198, 251, 0.08);
  bottom: -200px;
  right: -200px;
}

.mobile-hint {
  background: #fff;
  padding: 20px 28px;
  border-radius: 16px;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  max-width: 320px;
  z-index: 1;
}

.mobile-hint i {
  font-size: 32px;
  color: #0089ff;
  margin-bottom: 12px;
}

.mobile-hint p {
  color: #666;
  font-size: 14px;
  margin: 10px 0;
}
</style>
